import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { issuesAPI } from '../../services/api';
import { parseApiError } from '../../utils/errors';

const URGENCY_COLOR = { low: 'green', medium: 'blue', high: 'orange', critical: 'red' };
const STATUS_COLOR = { open: 'orange', in_progress: 'blue', resolved: 'green', closed: 'gray' };
const isClosed = (s) => s === 'resolved' || s === 'closed';

export default function HRIssues() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ status: '', hr_response: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = (s) => {
    setLoading(true);
    return issuesAPI.list(s ? { issue_status: s } : {})
      .then(({ data }) => { setIssues(data); return data; })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  // Auto-open a specific issue when navigated from a notification (?open=<id>).
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    issuesAPI.list().then(({ data }) => {
      const iss = data.find((i) => String(i.id) === String(openId));
      if (iss) openModal(iss);
      setSearchParams({}, { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (iss) => {
    setError('');
    setSelected(iss);
    setForm({ status: iss.status, hr_response: iss.hr_response || '' });
  };

  const reopen = async (iss) => {
    setError('');
    try {
      await issuesAPI.update(iss.id, { status: 'in_progress' });
      const data = await load(filter);
      const updated = (data || []).find((i) => i.id === iss.id);
      if (updated) openModal(updated);
    } catch (err) {
      setError(parseApiError(err, 'Failed to reopen issue.'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await issuesAPI.update(selected.id, form);
      setSelected(null);
      load(filter);
    } catch (err) {
      setError(parseApiError(err, 'Failed to update issue.'));
    } finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="page-header"><h2>Issue Management</h2></div>

      <div className="filter-bar">
        {['', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
          <button key={s} className={`filter-btn${filter === s ? ' active' : ''}`} onClick={() => setFilter(s)}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {loading ? <div className="page-loading">Loading...</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr><th>Title</th><th>Employee</th><th>Task</th><th>Urgency</th><th>Status</th><th>Date</th><th>Action</th></tr>
            </thead>
            <tbody>
              {issues.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No issues</td></tr>
              ) : issues.map((iss) => (
                <tr key={iss.id}>
                  <td>{iss.title}</td>
                  <td>{iss.raised_by?.full_name}</td>
                  <td>#{iss.task_id}</td>
                  <td><span className={`badge badge-${URGENCY_COLOR[iss.urgency]}`}>{iss.urgency}</span></td>
                  <td><span className={`badge badge-${STATUS_COLOR[iss.status]}`}>{iss.status}</span></td>
                  <td>{new Date(iss.created_at).toLocaleDateString()}</td>
                  <td>
                    {isClosed(iss.status) ? (
                      <button className="btn btn-sm btn-ghost" onClick={() => reopen(iss)}>Reopen</button>
                    ) : (
                      <button className="btn btn-sm btn-primary" onClick={() => openModal(iss)}>Respond</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Respond to Issue</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>{selected.title}</strong></p>
              <p className="text-muted">{selected.description}</p>
              {error && <div className="alert alert-danger">{error}</div>}
              <form onSubmit={handleUpdate}>
                <div className="form-group">
                  <label>Update Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>HR Response</label>
                  <textarea rows={4} value={form.hr_response} onChange={(e) => setForm({ ...form, hr_response: e.target.value })} />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setSelected(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Response'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
